import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useNotify } from 'react-admin';

import { deletePartnerWalletEntry, IWalletEntry, updatePartnerWalletEntry } from '../../Services/partners';

// Retouche ou suppression d'une ligne du wallet saisie par un admin (crédit offert,
// ajustement). Le montant est SIGNÉ, comme dans le formulaire d'ajout : > 0 crédit,
// < 0 débit. Un crédit offert reste positif : une reprise se saisit en ajustement.
//
// La suppression est LOGIQUE : la ligne sort des soldes et de l'historique, mais reste
// en base avec son auteur.

type Props = {
	partnerId: string;
	entry: IWalletEntry | null;
	onClose: () => void;
	onSaved: () => void;
};

// #region EditWalletEntryDialog
export const EditWalletEntryDialog: React.FC<Props> = ({ partnerId, entry, onClose, onSaved }) => {
	const notify = useNotify();
	const [amount, setAmount] = useState('');
	const [label, setLabel] = useState('');
	const [busy, setBusy] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);

	// Réinitialisé sur la ligne courante à chaque ouverture.
	useEffect(() => {
		if (!entry) return;
		setAmount(String(entry.kind === 'debit' ? -entry.amount : entry.amount));
		setLabel(entry.label || '');
		setConfirmDelete(false);
	}, [entry]);

	if (!entry) return null;
	const n = Number(amount);
	const granted = entry.category === 'granted';
	const amountError = amount === '' || Number.isNaN(n) || n === 0 || (granted && n < 0);

	const run = (p: Promise<unknown>, ok: string) => {
		setBusy(true);
		p.then(() => {
			notify(ok, { type: 'success' });
			onSaved();
			onClose();
		})
			.catch((e: any) => notify(`Erreur : ${e?.message || e}`, { type: 'error' }))
			.finally(() => setBusy(false));
	};

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>{granted ? 'Crédit offert' : 'Ajustement'}</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Saisi le {entry.createdAt?.slice(0, 10)}
					{entry.createdBy ? ` par ${entry.createdBy}` : ''}
					{entry.editedAt ? ` · retouché le ${entry.editedAt.slice(0, 10)} par ${entry.editedBy || '—'}` : ''}
				</Typography>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<TextField
						size="small"
						type="number"
						label="Montant (crédits)"
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						error={amountError}
						helperText={granted ? 'Un crédit offert reste positif.' : '> 0 = crédit, < 0 = débit'}
					/>
					<TextField size="small" label="Libellé" value={label} onChange={(e) => setLabel(e.target.value)} />
				</Box>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 3 }}>
					<Button
						color="error"
						disabled={busy}
						onClick={() =>
							confirmDelete
								? run(deletePartnerWalletEntry(partnerId, entry.id), 'Ligne supprimée')
								: setConfirmDelete(true)
						}
					>
						{confirmDelete ? 'Confirmer la suppression' : 'Supprimer la ligne'}
					</Button>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button onClick={onClose}>Fermer</Button>
						<Button
							variant="contained"
							disabled={busy || amountError}
							onClick={() => run(updatePartnerWalletEntry(partnerId, entry.id, n, label), 'Ligne mise à jour')}
						>
							Enregistrer
						</Button>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
// #endregion
